import builtIn from '../data/routines.json';
import type { Practice, Routine, Settings } from '../types';
import { getPractice } from './catalog';
import { hasSavedSetting, loadRoutineStore, loadSettings as loadPracticeSettings, updateRoutineStore } from './storage';
import { practiceSeconds } from './timeline';

export const DEFAULT_ROUTINE_ID = 'daily-sadhana';

const builtInRoutines = (): Routine[] =>
  (builtIn as { id: string; title: string; blurb: string; items: string[]; defaults?: Routine['defaults'] }[]).map(r => ({ ...r, builtIn: true }));

/** Built-in sessions with the user's edits applied, followed by the user's own. */
export function loadRoutines(): Routine[] {
  const { custom, edits } = loadRoutineStore();
  const base = builtInRoutines().map(r => {
    const e = edits[r.id];
    return e ? { ...r, title: e.title ?? r.title, items: (e.items ?? r.items).filter(id => getPractice(id)) } : r;
  });
  return [...base, ...custom.map(r => ({ ...r, items: r.items.filter(id => getPractice(id)) }))];
}

export const getRoutine = (id: string) => loadRoutines().find(r => r.id === id);

export const isEdited = (id: string) => Boolean(loadRoutineStore().edits[id]);

/** A practice's settings inside a session: the user's saved value, else the session's default, else the practice's own. */
export function settingsFor(routine: Routine | null | undefined, p: Practice): Settings {
  const settings = loadPracticeSettings(p);
  const d = routine?.defaults?.[p.id];
  if (!d) return settings;
  const patch = Object.fromEntries(Object.entries(d).filter(([k]) => !hasSavedSetting(p.id, k as keyof Settings)));
  return { ...settings, ...patch };
}

/** Seconds of one practice as the user has it set up. */
export const itemSeconds = (practiceId: string, routine?: Routine | null): number => {
  const p = getPractice(practiceId);
  return p ? practiceSeconds(p, settingsFor(routine, p)) : 0;
};

export const routineSeconds = (r: Routine) => r.items.reduce((n, id) => n + itemSeconds(id, r), 0);

export function setRoutineItems(id: string, items: string[]) {
  updateRoutineStore(s => {
    const custom = s.custom.find(r => r.id === id);
    if (custom) custom.items = items;
    else s.edits[id] = { ...s.edits[id], items };
  });
}

export function setRoutineTitle(id: string, title: string) {
  const clean = title.trim() || 'My session';
  updateRoutineStore(s => {
    const custom = s.custom.find(r => r.id === id);
    if (custom) custom.title = clean;
    else s.edits[id] = { ...s.edits[id], title: clean };
  });
}

export function createRoutine(): Routine {
  const routine: Routine = { id: `mine-${Date.now().toString(36)}`, title: 'My session', blurb: 'Your own order.', items: [], builtIn: false };
  updateRoutineStore(s => s.custom.push(routine));
  return routine;
}

export function deleteRoutine(id: string) {
  updateRoutineStore(s => {
    s.custom = s.custom.filter(r => r.id !== id);
  });
}

export function resetRoutine(id: string) {
  updateRoutineStore(s => {
    delete s.edits[id];
  });
}

export function rememberRoutine(id: string) {
  updateRoutineStore(s => {
    s.lastRoutine = id;
  });
}

export const lastRoutineId = () => loadRoutineStore().lastRoutine ?? DEFAULT_ROUTINE_ID;

export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) return items;
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
